import { DateObject, getToday, getTomorrow, getCurrentDateTimePlus15Mins } from '../utils/dateTimeUtils'
import {
    getPlannedPickupDateAndTime as getPlannedPickupDateAndTimeDHL,
    formatPlannedPickupDateAndTime,
} from '../utils/DHLDateTimeUtils'
import { getPlannedPickupDateAndTime as getPlannedPickupDateAndTimeUPS } from '../utils/UPSDateTimeUtils'
import { UPSPickupMessaging, DHLPickupMessaging } from '../utils/messagingUtils'
import { DHLRatesResponse } from '../types/Courier/DHL/DHLRatesResponse'
import { UPSTimeInTransitResponse } from '../types/Courier/UPS/UPSTimeInTransitResponse'
import { IPickUpDetails } from '../types/Courier/DHL/DHLPickUpDetails'
import { IDHLCommon } from '../types/Courier/DHL/DHLCommon'
import { IUPSCommon } from '../types/Courier/UPS/UPSCommon'
import { ICourierConfig, IWarehouseLocation } from '../types/config'
import { IShippingAddress } from '../types/index'
import { IGetPickupAvailability } from '../types/IGetPickupAvailability'
import { dateToObject } from '../utils/dateTimeUtils'
import { getReturnItemsValue } from '../utils/helpers'
import { raygun } from '@ynap/clientside-monitoring'

const reportToRaygun = (message, { requestBody, error }) =>
    raygun.helpers.agent('send', {
        error: new Error(message),
        tags: [
            requestBody.courier,
            'book-a-return',
            `${requestBody.courier}-pickup-availability-error`,
            requestBody.country,
        ],
        customData: {
            requestBody,
            errorString: error.toString(),
        },
    })

const getPickupAvailability = (
    config: any,
    messages: any,
    shippingAddress: IShippingAddress,
    userDate: Date | undefined,
    returnItems: any[],
    returnCourier: string
): Promise<IGetPickupAvailability> => {
    const clientId = config.global?.services.commerce.clientId
    const { collectionService, returnWarehouseCountryConfig } = config.head
    const { warehouses, couriers, noCustomsDeclarableEU = [] } = collectionService
    const returnCountry = returnWarehouseCountryConfig[shippingAddress?.country]
    const warehouseConfig = warehouses[returnCountry]
    const courierConfig: ICourierConfig = couriers[returnCourier]

    const dateToday: DateObject = getToday()
    const dateTomorrow: DateObject = getTomorrow()
    const dateSelected = userDate ? dateToObject(userDate) : dateToday

    if (returnCourier === 'DHL') {
        return fetchDHLAvailability(shippingAddress, courierConfig, warehouseConfig, dateSelected, messages, noCustomsDeclarableEU, clientId)
            .then((availability) => {
                if (availability.missedCutOff) {
                    return fetchDHLAvailability(shippingAddress, courierConfig, warehouseConfig, dateTomorrow, messages, noCustomsDeclarableEU, clientId)
                }
                return availability
            })
            .catch(() => ({ error: true })) // sends error to the component
    }
    // if returnCourier === "UPS"
    return fetchUPSAvailability(shippingAddress, courierConfig, warehouseConfig, dateSelected, messages, returnItems, clientId)
        .then((availability) => {
            if (availability.missedCutOff) {
                return fetchUPSAvailability(
                    shippingAddress,
                    courierConfig,
                    warehouseConfig,
                    dateTomorrow,
                    messages,
                    returnItems,
                    clientId
                )
            }
            return availability
        })
        .catch(() => ({ error: true })) // sends error to the component
}

export default getPickupAvailability

//fetches
const fetchDHLAvailability = (
    shippingAddress: IShippingAddress,
    config: ICourierConfig,
    warehouseConfig: Record<string, IWarehouseLocation>,
    date: DateObject,
    messages: any,
    noCustomsDeclarableEU: string[],
    clientId: string
): Promise<IDHLCommon> => {
    const { dimensions, accountId, baseUrl, accountIdsOnCountry = [] } = config
    const { weight, width, height, length, unitOfMeasurement } = dimensions
    const { city, country, zipCode } = shippingAddress
    const destinationCountryCode = warehouseConfig.country
    const destinationPostalCode = warehouseConfig.postCode || '0000'
    const destinationCityName = warehouseConfig.city

    const plannedShippingDate = `${date.year}-${date.month}-${date.day}`

    const isCustomsDeclarable = !(country.toString() === destinationCountryCode.toString()
        || (noCustomsDeclarableEU?.length && noCustomsDeclarableEU?.includes(country.toString()) 
            && noCustomsDeclarableEU?.includes(destinationCountryCode.toString())))
    const nextBusinessDay = true

    const originPostalCode = zipCode === '' ? '0000' : zipCode
    const accountNumber = accountIdsOnCountry?.[country] !== undefined ? accountIdsOnCountry[country] : accountId

    let jsonResponse
    const pickupInfo = fetch(
        `${baseUrl}/rates?accountNumber=${accountNumber}&originCountryCode=${country}&originPostalCode=${originPostalCode}&originCityName=${city}&destinationCountryCode=${destinationCountryCode}&destinationPostalCode=${destinationPostalCode}&destinationCityName=${destinationCityName}&weight=${weight}&length=${length}&width=${width}&height=${height}&plannedShippingDate=${plannedShippingDate}&isCustomsDeclarable=${isCustomsDeclarable}&unitOfMeasurement=${unitOfMeasurement}&nextBusinessDay=${nextBusinessDay}`,
        {
            headers: {
                'Content-Type': 'application/json',
                'x-ibm-client-id': clientId,
            },
        }
    )
        .then(async (res) => {
            if (!res.ok) {
                const error = await res.text()
                throw new Error(error)
            }
            return res.json()
        })
        .then((json: DHLRatesResponse) => {
            jsonResponse = json
            return mapDHLToCommon(json, messages, date)
        })
        .catch((e) => {
            reportToRaygun(`Unsucessful response when hitting ${baseUrl}/rates`, {
                requestBody: {
                    courier: 'DHL',
                    country: country,
                    originPostalCode: originPostalCode,
                    city: city,
                    destinationCountryCode,
                    destinationPostalCode,
                    destinationCityName,
                    plannedShippingDate,
                    hasProducts: !!jsonResponse?.products,
                    productLength: jsonResponse?.products ? jsonResponse?.products?.length : 0,
                },
                error: e,
            })
            throw new Error(e)
        })

    return pickupInfo
}

const fetchUPSAvailability = (
    shippingAddress: IShippingAddress,
    config: ICourierConfig,
    warehouseConfig: Record<string, IWarehouseLocation>,
    date: DateObject,
    messages: any,
    returnItems: any[],
    clientId: string
): Promise<IUPSCommon> => {
    const { dimensions, baseUrl } = config
    const { weight, unitOfMeasurement } = dimensions
    const { city, state, country, zipCode } = shippingAddress

    const destinationCountryCode = warehouseConfig.country
    const destinationPostalCode = warehouseConfig.postCode
    const destinationStateName = warehouseConfig.state
    const destinationCityName = warehouseConfig.city

    const requestDateFormatted = `${date.year}${date.month}${date.day}`

    const valueOfGoods = getReturnItemsValue(returnItems)
    const currencyOfGoods = returnItems[0].price.currency.label

    const body = {
        TimeInTransitRequest: {
            Request: {
                RequestOption: 'TNT',
            },
            ShipFrom: {
                Address: {
                    City: city,
                    StateProvinceCode: state?.length >= 2 ? state : '', // UPS only allow state code (e.g. NJ) or province (for Ireland)
                    CountryCode: country,
                    PostalCode: zipCode,
                },
            },
            ShipTo: {
                Address: {
                    City: destinationCityName,
                    StateProvinceCode: destinationStateName,
                    CountryCode: destinationCountryCode,
                    PostalCode: destinationPostalCode,
                    ResidentialAddressIndicator: 'commercial',
                },
            },
            Pickup: {
                Date: requestDateFormatted, //20210126
            },
            ShipmentWeight: {
                UnitOfMeasurement: {
                    Code: unitOfMeasurement,
                    Description: unitOfMeasurement,
                },
                Weight: weight,
            },
            InvoiceLineTotal: {
                MonetaryValue: valueOfGoods.toString(),
                CurrencyCode: currencyOfGoods,
            },
        },
    }

    let jsonResponse

    const pickupInfo = fetch(`${baseUrl}/availability`, {
        method: 'POST',
        headers: {
            'Access-Control-Allow-Origin': '*',
            'Content-Type': 'application/json',
            'x-ibm-client-id': clientId,
        },
        body: JSON.stringify(body),
    })
        .then(async (res) => {
            if (!res.ok) {
                const error = await res.text()
                throw new Error(error)
            }
            return res.json()
        })
        .then((json: UPSTimeInTransitResponse) => {
            jsonResponse = json
            return mapUPSToCommon(json, shippingAddress, messages, date)
        })
        .catch((e) => {
            reportToRaygun(`Unsucessful response when hitting ${baseUrl}/lion/availability`, {
                requestBody: {
                    courier: 'UPS',
                    country: country,
                    requestBody: body,
                    hasProducts: !!jsonResponse?.products,
                    productLength: jsonResponse?.products ? jsonResponse?.products?.length : 0,
                },
                error: e,
            })
            throw new Error(e)
        })

    return pickupInfo
}

//mappers
const mapDHLToCommon = (data: DHLRatesResponse, messages: any, dateRequested: DateObject): IDHLCommon => {
    const currentDateTime: Date = getCurrentDateTimePlus15Mins()

    const { missedCutOff, plannedPickupDateAndTime, pickupLatest, productCode }: IPickUpDetails =
        getPlannedPickupDateAndTimeDHL(data, currentDateTime)

    const pickupMessages = new DHLPickupMessaging(messages, pickupLatest, currentDateTime, plannedPickupDateAndTime)
    const timePeriod = pickupMessages.formatTimePeriod()
    const datePeriod = pickupMessages.formatDatePeriod()

    const formattedPlannedPickupDateAndTime = formatPlannedPickupDateAndTime(plannedPickupDateAndTime)

    const isPlannedPickupRequestedDate =
        dateRequested.day === String(plannedPickupDateAndTime.getDate()).padStart(2, '0')
    return {
        timePeriod,
        datePeriod,
        dateRequested: new Date(
            dateRequested.year,
            Number(dateRequested.month) - 1,
            Number(dateRequested.day),
            Number(dateRequested.hour),
            Number(dateRequested.minutes)
        ),
        missedCutOff,
        plannedPickupDateAndTime: formattedPlannedPickupDateAndTime,
        productCode,
        isPlannedPickupRequestedDate,
    }
}

const mapUPSToCommon = (
    data: UPSTimeInTransitResponse,
    shippingAddress: IShippingAddress,
    messages: any,
    dateRequested: DateObject
): IUPSCommon => {
    const currentDateTime: Date = getCurrentDateTimePlus15Mins()
    const { year, month, day, hour, minutes } = dateToObject(currentDateTime)
    const currentTime = `${hour}${minutes}`
    const currentDate = `${year}${month}${day}`

    const { missedCutOff, plannedPickupDateAndTime, isPickupEarliestNow, productCode } = getPlannedPickupDateAndTimeUPS(
        data,
        shippingAddress,
        currentDate,
        currentTime
    )

    const pickupMessages = new UPSPickupMessaging(messages, isPickupEarliestNow, currentDate, plannedPickupDateAndTime)
    const timePeriod = pickupMessages.formatTimePeriod()
    const datePeriod = pickupMessages.formatDatePeriod()

    const requestedDate = `${dateRequested.year}${dateRequested.month}${dateRequested.day}`
    const isPlannedPickupRequestedDate = requestedDate === plannedPickupDateAndTime.PickupDate

    return {
        timePeriod,
        datePeriod,
        dateRequested: new Date(
            dateRequested.year,
            Number(dateRequested.month) - 1,
            Number(dateRequested.day),
            Number(dateRequested.hour),
            Number(dateRequested.minutes)
        ),
        missedCutOff,
        productCode,
        plannedPickupDateAndTime,
        isPlannedPickupRequestedDate,
    }
}
